import { useRef, useState } from 'react'
import { Avatar, Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import { Camera } from 'lucide-react'

import { useAuth } from '../../../context/SecurityContextProvider'
import { uploadProfilePicture, updateBio } from '../../../services/api/UserDataService'
import { resolveMediaUrl } from '../../../utils/media/resolveMediaUrl'
import { CropModal } from '../../studio/tabs/settings/CropModal'
import { onboardingStyles as s, ACCENT } from '../onboardingStyles'

const MAX_BIO_LEN = 50

export default function FinalTouchesStep({ userData, goNext }) {
    const { setUserData } = useAuth()
    const inputRef = useRef(null)
    const [picture, setPicture] = useState(userData?.profilePicture || '')
    const [bio, setBio] = useState(userData?.bio || '')
    const [uploadingPic, setUploadingPic] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [imageSrc, setImageSrc] = useState(null)
    const [fileName, setFileName] = useState('')
    const [cropOpen, setCropOpen] = useState(false)

    const username = userData?.username

    // Open the cropper with the chosen image; the upload happens after the crop is confirmed.
    const handlePicSelected = (event) => {
        setError('')
        const file = event.target.files && event.target.files[0]
        event.target.value = '' // reset so picking the same file again still fires onChange
        if (!file) return
        if (!file.type.startsWith('image/')) {
            setError('Please choose an image file.')
            return
        }
        const reader = new FileReader()
        reader.onload = (e) => {
            setImageSrc(e.target.result)
            setFileName(file.name)
            setCropOpen(true)
        }
        reader.readAsDataURL(file)
    }

    const handleCropComplete = async (croppedBlob) => {
        setCropOpen(false)
        setUploadingPic(true)
        const file = new File([croppedBlob], fileName || 'profile.jpg', { type: 'image/jpeg' })
        const formData = new FormData()
        formData.append('file', file)
        try {
            // New API: POST /users/me/profile-picture returns the updated user (relative pfp URL).
            const updated = await uploadProfilePicture(formData)
            setUserData(updated)
            setPicture(updated.profilePicture || '')
        } catch {
            setError('Photo upload failed. Please try again.')
        } finally {
            setUploadingPic(false)
        }
    }

    const handleContinue = async () => {
        setSaving(true)
        const trimmed = bio.trim()
        if (trimmed && trimmed !== (userData?.bio || '')) {
            try {
                const updated = await updateBio(trimmed) // PUT /users/me/bio → updated user
                setUserData(updated)
            } catch {
                // non-blocking: a bio hiccup shouldn't trap the user on this step
            }
        }
        goNext()
    }

    return (
        <Box>
            <Typography sx={s.title}>Final touches</Typography>
            <Typography sx={s.subtitle}>Add a profile picture and bio before your page goes live.</Typography>

            <Typography sx={{ fontFamily: 'Catamaran', fontWeight: 700, color: 'text.gray', fontSize: '0.8rem', mb: 0.75 }}>
                PREVIEW
            </Typography>
            <Box
                sx={{
                    background: 'linear-gradient(160deg, #2b2b2b 0%, #131313 100%)',
                    borderRadius: '24px',
                    p: 3,
                    mb: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                }}
            >
                <Box sx={{ position: 'relative' }}>
                    <Avatar
                        src={resolveMediaUrl(picture)}
                        alt={username}
                        sx={{ width: 88, height: 88, border: '3px solid rgba(255,255,255,0.9)' }}
                    />
                    <Box
                        onClick={() => !uploadingPic && inputRef.current?.click()}
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            backgroundColor: ACCENT,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: '2px solid #fff',
                        }}
                    >
                        {uploadingPic ? <CircularProgress size={15} color="inherit" /> : <Camera size={15} />}
                    </Box>
                </Box>
                <Typography sx={{ fontFamily: 'Catamaran', fontWeight: 800, color: '#fff', mt: 1.2 }}>
                    @{username}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: 'Catamaran',
                        color: bio.trim() ? 'rgba(255,255,255,0.78)' : 'rgba(255,255,255,0.4)',
                        fontSize: '0.9rem',
                        mt: 0.3,
                        maxWidth: 320,
                    }}
                >
                    {bio.trim() || 'Your bio will appear here'}
                </Typography>
            </Box>

            <input ref={inputRef} type="file" accept="image/*" hidden onChange={handlePicSelected} />

            <Button
                onClick={() => inputRef.current?.click()}
                disabled={uploadingPic}
                startIcon={<Camera size={16} />}
                sx={{ textTransform: 'none', fontFamily: 'Catamaran', fontWeight: 700, color: ACCENT, mb: 1 }}
            >
                {picture ? 'Change photo' : 'Add a photo'}
            </Button>

            <TextField
                fullWidth
                label="Bio"
                placeholder="link in bio :)"
                value={bio}
                onChange={(e) => e.target.value.length <= MAX_BIO_LEN && setBio(e.target.value)}
                helperText={`${bio.length}/${MAX_BIO_LEN}`}
                FormHelperTextProps={{ sx: { textAlign: 'right', m: 0 } }}
            />

            {error && (
                <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 600, mt: 1, textAlign: 'center' }}>
                    {error}
                </Typography>
            )}

            <Box sx={s.footer}>
                <Button variant="contained" disableElevation onClick={handleContinue} disabled={saving || uploadingPic} sx={s.ctaButton}>
                    {saving ? 'Saving…' : 'Continue'}
                </Button>
                <Button onClick={goNext} disabled={saving || uploadingPic} sx={s.skipButton}>
                    Skip for now
                </Button>
            </Box>

            <CropModal
                open={cropOpen}
                imageSrc={imageSrc}
                onClose={() => setCropOpen(false)}
                onCropComplete={handleCropComplete}
            />
        </Box>
    )
}
